import { deepEqualExclusionConditions } from './helpers'
import { FILE_STATE } from './types'
import {
  GENERIC_STATUS_IMPORT_PENDING,
  GENERIC_STATUS_IMPORTED,
  GENERIC_STATUS_REIMPORT_PENDING,
  GENERIC_STATUS_VERIFICATION_ERROR
} from '../../AnalysisData/extra/types'

const STATUS_ROLLBACK_SOURCES = new Set([
  GENERIC_STATUS_IMPORT_PENDING,
  GENERIC_STATUS_REIMPORT_PENDING,
  GENERIC_STATUS_IMPORTED,
  GENERIC_STATUS_VERIFICATION_ERROR
])

const POLL_INTERVAL_MS = 2000
const ROLLBACK_WAIT_TIMEOUT_MS = 120000

/**
 * @param {import('./types').FileObject | null | undefined} file
 * @returns {string | null}
 */
function primaryKeyConversionTableKey(file) {
  if (file === null || file === undefined) {
    return null
  }
  const raw = file.idConversionTable
  if (raw === null || raw === undefined) {
    return null
  }
  const key = String(raw).trim()
  if (key === '') {
    return null
  }
  return key
}

/**
 * @param {import('./types').FileObject} current
 * @param {import('./types').FileObject} existing
 * @returns {boolean}
 */
function resolveIsBaseForExclusionRefresh(current, existing) {
  if (current.isBaseFile === true) {
    return true
  }
  if (current.isBaseFile === false) {
    return false
  }
  if (existing.isBaseFile === true) {
    return true
  }
  return false
}

/**
 * @param {import('./types').FileObject} current
 * @param {import('./types').FileObject | null | undefined} existing
 * @returns {boolean}
 */
function needsExclusionRefreshOnRegister(current, existing) {
  if (current.fileState !== FILE_STATE.UPLOADED) {
    return false
  }
  if (existing === null || existing === undefined || existing.fileState !== FILE_STATE.UPLOADED) {
    return false
  }
  if (!resolveIsBaseForExclusionRefresh(current, existing)) {
    return false
  }
  return !deepEqualExclusionConditions(current.exclusionConditions, existing.exclusionConditions)
}

/**
 * Uploaded 同士の更新でベースファイルフラグが変更されたか。 変更時は IF02080 が GMR 再構築とステータス巻き戻しを行う（除外条件変更時と同様）。
 *
 * @param {import('./types').FileObject} current
 * @param {import('./types').FileObject | null | undefined} existing
 * @returns {boolean}
 */
function baseFlagChangedOnRegister(current, existing) {
  if (current.fileState !== FILE_STATE.UPLOADED) {
    return false
  }
  if (existing === null || existing === undefined || existing.fileState !== FILE_STATE.UPLOADED) {
    return false
  }
  if (current.isBaseFile === null || current.isBaseFile === undefined) {
    return false
  }
  return (current.isBaseFile === true) !== (existing.isBaseFile === true)
}

/**
 * @param {import('./types').FileObject} current
 * @param {import('./types').FileObject | null | undefined} existing
 * @returns {boolean}
 */
function needsConversionRefreshWithStatusReset(current, existing) {
  if (current.fileState !== FILE_STATE.UPLOADED) {
    return false
  }
  if (existing === null || existing === undefined || existing.fileState !== FILE_STATE.UPLOADED) {
    return false
  }
  const oldKey = primaryKeyConversionTableKey(existing)
  const newKey = primaryKeyConversionTableKey(current)
  if (oldKey === newKey) {
    return false
  }
  return current.isBaseFile === true
}

/**
 * 登録済みベースファイルの削除か。 削除時も GMR 再構築（別ベース残存時は SF）またはトランザクション内巻き戻し（0件時）が行われるため、 ベースフラグ変更時と同様にステータス巻き戻し完了を待つ。
 *
 * @param {import('./types').FileObject} current
 * @param {import('./types').FileObject | null | undefined} existing
 * @returns {boolean}
 */
function baseFileDeletedOnRegister(current, existing) {
  if (current.fileState !== FILE_STATE.PENDING_DELETION) {
    return false
  }
  if (existing === null || existing === undefined || existing.fileState !== FILE_STATE.UPLOADED) {
    return false
  }
  return existing.isBaseFile === true
}

/**
 * @param {import('./types').FileObject[]} changedFiles
 * @returns {boolean}
 */
export function hasPendingVirusCheckAmongChanged(changedFiles) {
  for (const file of changedFiles) {
    if (file.fileState === FILE_STATE.PENDING_VIRUS_CHECK) {
      return true
    }
  }
  return false
}

/**
 * 除外・変換表登録後にステータス巻き戻し完了を待つ必要があるか（IF2080 側の uploaded 同士リフレッシュと整合）
 *
 * @param {import('./types').FileObject[]} changedFiles
 * @param {Record<string, import('./types').FileObject> | null | undefined} savedUploadState
 * @returns {boolean}
 */
export function needsStatusRollbackWaitOnRegister(changedFiles, savedUploadState) {
  if (hasPendingVirusCheckAmongChanged(changedFiles)) {
    return false
  }
  for (const file of changedFiles) {
    const existing =
      savedUploadState !== null && savedUploadState !== undefined ? savedUploadState[file.fileKey] : undefined
    if (file.fileState === FILE_STATE.PENDING_DELETION) {
      if (baseFileDeletedOnRegister(file, existing)) {
        return true
      }
      continue
    }
    if (needsExclusionRefreshOnRegister(file, existing)) {
      return true
    }
    if (needsConversionRefreshWithStatusReset(file, existing)) {
      return true
    }
    if (baseFlagChangedOnRegister(file, existing)) {
      return true
    }
  }
  return false
}

/**
 * IF02095 の generic_data_kinds から、ベース変更時にバックエンドが巻き戻すデータ分類コードの集合を返す。 （is_base_reset_target:
 * 従業員ID単一PK＋資格情報・兼務情報。共通レイヤーの判定をAPIが返す）
 *
 * @param {{ genericDataKindCode?: string; isBaseResetTarget?: boolean }[] | null | undefined} genericDataKinds
 * @returns {Set<string>}
 */
export function baseResetTargetCodes(genericDataKinds) {
  /** @type {Set<string>} */
  const codes = new Set()
  for (const kind of genericDataKinds ?? []) {
    if (kind?.isBaseResetTarget === true && kind.genericDataKindCode) {
      codes.add(String(kind.genericDataKindCode))
    }
  }
  return codes
}

/**
 * 監視対象は「巻き戻し対象ステータス かつ バックエンドが巻き戻す分類（targetCodes）」に限定する。 従業員IDをキーに持たない分類は巻き戻されないため、監視すると永遠に完了しない（バグ#3233）。
 *
 * @param {Record<string, string> | null | undefined} statusByDataKind
 * @param {Set<string> | null | undefined} targetCodes - 巻き戻し対象のデータ分類コード。null/undefined の場合は全分類を対象とする（後方互換）
 * @returns {Record<string, string>}
 */
export function snapshotStatusRollbackWatch(statusByDataKind, targetCodes = null) {
  /** @type {Record<string, string>} */
  const watch = {}
  if (statusByDataKind === null || statusByDataKind === undefined) {
    return watch
  }
  for (const [code, status] of Object.entries(statusByDataKind)) {
    if (targetCodes !== null && targetCodes !== undefined && !targetCodes.has(code)) {
      continue
    }
    const current = String(status)
    if (STATUS_ROLLBACK_SOURCES.has(current)) {
      watch[code] = current
    }
  }
  return watch
}

/**
 * 監視対象の全分類が巻き戻し対象ステータス（更新待ち・再更新待ち・更新済・検証エラー）から離脱したら完了とする。 バックエンドはベース削除に伴うマッピング解除で「設定中」へ再判定することがあり（設定中を優先）、
 * 「検証待ち」のみを完了とみなすと永遠に完了せずタイムアウトする（バグ#3262）。 離脱後のステータスは「検証待ち」「設定中」いずれも完了扱いとする。
 *
 * @param {Record<string, string>} watchSnapshot
 * @param {Record<string, string> | null | undefined} statusByDataKind
 * @returns {boolean}
 */
export function isStatusRollbackWatchComplete(watchSnapshot, statusByDataKind) {
  const codes = Object.keys(watchSnapshot)
  if (codes.length === 0) {
    return true
  }
  if (statusByDataKind === null || statusByDataKind === undefined) {
    return false
  }
  for (const code of codes) {
    const current = statusByDataKind[code]
    if (current === undefined) {
      continue
    }
    if (STATUS_ROLLBACK_SOURCES.has(String(current))) {
      return false
    }
  }
  return true
}

/**
 * @param {{
 *   fetchScreen: () => Promise<{ statusByDataKind: Record<string, string> | null | undefined }>
 *   watchSnapshot: Record<string, string>
 * }} params
 * @returns {Promise<void>}
 */
export async function waitForGenericTaskStatusRollback(params) {
  const { fetchScreen, watchSnapshot } = params
  const deadline = Date.now() + ROLLBACK_WAIT_TIMEOUT_MS
  while (Date.now() < deadline) {
    const screen = await fetchScreen()
    if (isStatusRollbackWatchComplete(watchSnapshot, screen.statusByDataKind)) {
      return
    }
    await new Promise((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS)
    })
  }
  throw new Error('STATUS_ROLLBACK_WAIT_TIMEOUT')
}
